const template = document.createElement("template");

template.innerHTML = /* HTML */ `
  <style>
    @media (max-width: 767px) {
      :host {
        --scroll-padding: 10rem;
        --range-start: 1.5rem; /* offset breadcrumb height to start progress animation */

        background-color: var(--article-progress-bar-bg-color);
        block-size: 0.25rem;
        inset: 0;
        position: fixed;
        z-index: 3;
      }

      :host::before {
        animation-fill-mode: both;
        animation-name: scroll-progress;
        animation-range-end: exit 50vh;
        animation-range-start: contain
          calc(var(--range-start) + var(--scroll-padding));
        animation-timeline: --article-progress-bar;
        background-color: var(--article-progress-bar-fill-bg-color);
        block-size: 100%;
        content: "";
        display: block;
        transform-origin: left;
      }

      :host([opulent]) {
        --range-start: calc(
          2.5rem + 3.75rem + 2.5rem
        ); /* offset spacings and feature bar to start progress animation */

        animation-fill-mode: both;
        animation-name: scroll-progress-opulent;
        animation-range-start: contain var(--scroll-padding);
        animation-timeline: --article-progress-bar;
      }

      @keyframes scroll-progress {
        from {
          transform: scaleX(0);
        }

        to {
          transform: scaleX(1);
        }
      }

      @keyframes scroll-progress-opulent {
        from {
          display: none;
        }

        to {
          display: block;
        }
      }
    }
  </style>
`;

const globalStyles = document.createElement("style");
globalStyles.innerHTML = /* CSS */ `
  body {
    timeline-scope: --article-progress-bar;
  }

  [data-progress-bar-subject] {
    view-timeline-name: --article-progress-bar;
  }
`;

export class ArticleProgressBar extends HTMLElement {
  #subject: HTMLElement | undefined | null;
  #resizeObserver: ResizeObserver | undefined;
  #visibilityThreshold = 2;

  constructor() {
    super();

    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));

    document.head.appendChild(globalStyles);
    this.#subject = document.querySelector<HTMLElement>(
      "[data-progress-bar-subject]",
    );
  }

  connectedCallback() {
    this.#setVisibility();
    this.#resizeObserver = new ResizeObserver(() => {
      this.#setVisibility();
    });
    this.#resizeObserver.observe(document.body);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  #setVisibility() {
    const subjectHeight = this.#subject?.offsetHeight;
    if (!subjectHeight) return;
    const viewportHeight = window.innerHeight;

    if (subjectHeight > viewportHeight * this.#visibilityThreshold) {
      this.shadowRoot?.host.removeAttribute("hidden");
    } else {
      this.shadowRoot?.host.setAttribute("hidden", "");
    }
  }
}

"customElements" in window &&
  /* Not supported in Firefox (yet: https://bugzilla.mozilla.org/show_bug.cgi?id=1676780) and iOS18 or older. */
  CSS.supports("animation-timeline", "auto") &&
  customElements.get("ws-article-progress-bar") === undefined &&
  customElements.define("ws-article-progress-bar", ArticleProgressBar);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-article-progress-bar": ArticleProgressBar;
  }
}
